import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createSocket } from '../utils/socket';
import {
  loadPendingTasks,
  addPendingTask,
  removePendingTask,
} from '../utils/pendingCreationTasks';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

function stepLabel(step, chapterNumber) {
  if (step === 'summarize') return 'Learning objectives';
  if (step === 'outline') return 'Outline';
  if (step === 'outline_add_one') return 'Add outline chapter';
  if (step === 'chapter') return chapterNumber != null ? `Chapter ${chapterNumber}` : 'Chapter';
  if (step === 'conclusion') return 'Conclusion';
  if (step === 'introduction') return 'Introduction';
  return step;
}

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, currentUserId } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [pendingTasks, setPendingTasks] = useState(() => loadPendingTasks());
  const socketRef = useRef(null);
  const completionListenersRef = useRef(new Map());

  const addNotification = useCallback((payload) => {
    const id = `n-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const notification = {
      id,
      gameId: payload.game_id,
      gameTitle: payload.gameTitle || '',
      step: payload.step,
      chapterNumber: payload.chapter_number,
      status: payload.status || 'complete',
      result: payload.result,
      error: payload.error,
      timestamp: Date.now(),
      read: false,
    };
    setNotifications((prev) => [...prev, notification]);
    const key = payload.step === 'chapter' && payload.chapter_number != null
      ? `${payload.game_id}:${payload.step}:${payload.chapter_number}`
      : `${payload.game_id}:${payload.step}`;
    const listener = completionListenersRef.current.get(key);
    if (listener && payload.status === 'complete' && payload.result) {
      try {
        listener(payload.result, payload);
      } catch (e) {
        console.error('Completion listener error:', e);
      }
    }
  }, []);

  const markRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const addPending = useCallback((gameId, step, gameTitle = '', chapterNumber = null) => {
    const tasks = addPendingTask(gameId, step, gameTitle, chapterNumber);
    setPendingTasks(tasks);
  }, []);

  const removePending = useCallback((gameId, step, chapterNumber = null) => {
    removePendingTask(gameId, step, chapterNumber);
    setPendingTasks(loadPendingTasks());
  }, []);

  const isPending = useCallback((gameId, step, chapterNumber = null) => {
    return pendingTasks.some((t) => {
      if (t.gameId !== gameId || t.step !== step) return false;
      if (step === 'chapter') return chapterNumber == null || t.chapterNumber === chapterNumber;
      return true;
    });
  }, [pendingTasks]);

  const addCompletionListener = useCallback((gameId, step, callback, chapterNumber = null) => {
    const key = step === 'chapter' && chapterNumber != null
      ? `${gameId}:${step}:${chapterNumber}`
      : `${gameId}:${step}`;
    completionListenersRef.current.set(key, callback);
    return () => completionListenersRef.current.delete(key);
  }, []);

  const removeCompletionListener = useCallback((gameId, step, chapterNumber = null) => {
    const key = step === 'chapter' && chapterNumber != null
      ? `${gameId}:${step}:${chapterNumber}`
      : `${gameId}:${step}`;
    completionListenersRef.current.delete(key);
  }, []);

  // Sync pending from storage (e.g. after another tab or refresh)
  useEffect(() => {
    setPendingTasks(loadPendingTasks());
  }, []);

  // Refs so the socket handlers always call the latest versions without being effect dependencies
  const addNotificationRef = useRef(addNotification);
  addNotificationRef.current = addNotification;
  const removePendingRef = useRef(removePending);
  removePendingRef.current = removePending;

  // Socket: effect only re-runs when isAuthenticated or currentUserId changes
  useEffect(() => {
    if (!isAuthenticated || !currentUserId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const socket = createSocket();
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[NotificationContext] Socket connected, joining room user_' + currentUserId);
      socket.emit('join_user_room', { user_id: currentUserId });
    });

    socket.on('creation_step_complete', (payload) => {
      console.log('[NotificationContext] Received creation_step_complete', payload);
      const tasks = loadPendingTasks();
      const pending = tasks.find(
        (t) =>
          t.gameId === payload.game_id &&
          t.step === payload.step &&
          (payload.step !== 'chapter' || t.chapterNumber === payload.chapter_number)
      );
      const gameTitle = pending?.gameTitle || '';
      removePendingRef.current(payload.game_id, payload.step, payload.chapter_number);
      addNotificationRef.current({ ...payload, status: 'complete', gameTitle });
    });

    socket.on('creation_step_failed', (payload) => {
      console.log('[NotificationContext] Received creation_step_failed', payload);
      const tasks = loadPendingTasks();
      const pending = tasks.find(
        (t) =>
          t.gameId === payload.game_id &&
          t.step === payload.step &&
          (payload.step !== 'chapter' || t.chapterNumber === payload.chapter_number)
      );
      const gameTitle = pending?.gameTitle || '';
      removePendingRef.current(payload.game_id, payload.step, payload.chapter_number);
      addNotificationRef.current({ ...payload, status: 'failed', gameTitle });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentUserId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const value = {
    notifications,
    unreadCount,
    markRead,
    clearAll,
    addPending,
    pendingTasks,
    isPending,
    stepLabel,
    addCompletionListener,
    removeCompletionListener,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
